/**
 * Typed wrapper for the `/salescoach/api/feedback/cards` endpoint (Plan 04-07 Task 1).
 *
 * Mirrors `quality-api.ts` conventions:
 *   1. `buildQuery(filters)` strips defaults + resolves preset→from/to for the
 *      global filter strip (IA §1.2 filters).
 *   2. Feedback-specific knobs (sort/limit/offset/minRating/maxRating/hasText/
 *      userId) are appended as extra querystring params; only defined values
 *      are serialised so a canonical URL never carries `limit=undefined`.
 *   3. Return type is `ApiEnvelope<FeedbackCardsResponse>` — unwrapping is the
 *      hook's job (`use-feedback.ts`).
 */

import { apiFetch } from '@/services/api';
import { buildApiUrl } from '@/services/api-client';
import type { Filters } from '@/lib/filters-schema';
import type {
  ApiEnvelope,
  FeedbackCardsResponse,
  FeedbackSort,
  FeedbackSource,
} from './api-types';

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

/** Feedback-specific request parameters — matches `feedbackQuerySchema`
 *  on the backend (`src/dashboard/routes/api/feedback.ts`). */
export interface FetchFeedbackCardsParams {
  sort?: FeedbackSort;
  limit?: number;
  offset?: number;
  minRating?: number;
  maxRating?: number;
  hasText?: boolean;
  userId?: string;
  /** K2 — feedback source partition (all / chat / brief). */
  source?: FeedbackSource;
}

// ---------------------------------------------------------------------------
// Endpoint wrapper
// ---------------------------------------------------------------------------

/**
 * Build `/salescoach/api/feedback/cards?...` and fetch. Only params with a defined
 * value are appended so the URL stays short and the server's default-branch
 * (triage / limit=50 / offset=0) is exercised whenever the caller passes
 * undefined.
 *
 * The global filter strip (role / cohort / users / channel / preset / from /
 * to) is serialised by `buildApiUrl`; feedback-only params are layered on top.
 */
export async function fetchFeedbackCards(
  filters: Filters,
  params: FetchFeedbackCardsParams = {},
): Promise<ApiEnvelope<FeedbackCardsResponse>> {
  return apiFetch<ApiEnvelope<FeedbackCardsResponse>>(
    buildApiUrl('/salescoach/api/feedback/cards', filters, {
      sort: params.sort,
      limit: params.limit,
      offset: params.offset,
      minRating: params.minRating,
      maxRating: params.maxRating,
      hasText: params.hasText,
      userId: params.userId,
      // K2 — source partition flows through the same extras channel.
      source: params.source,
    }),
  );
}
